<template>
  <a-modal
    :title="l('AuditTeam_DetailTitle')"
    :width="900"
    :visible="visible"
    :footer="null"
    :maskClosable="false"
    @cancel="handleCancel"
  >
    <a-spin :spinning="loading">
      <template v-if="teamData">
        <!-- 基本信息 -->
        <a-card :title="l('AuditTeam_BasicInfo')" :bordered="false" size="small">
          <a-descriptions :column="2" bordered size="small">
            <a-descriptions-item :label="l('AuditTeam_TeamName')" :span="2">
              <span class="field-value">{{ teamData.name || '-' }}</span>
            </a-descriptions-item>
            <a-descriptions-item :label="l('AuditTeam_Description')" :span="2">
              <span class="field-value">{{ teamData.description || '-' }}</span>
            </a-descriptions-item>
            <a-descriptions-item :label="l('AuditTeam_TeamLeader')">
              <span class="field-value">{{ teamData.leaderName || '-' }}</span>
            </a-descriptions-item>
            <a-descriptions-item :label="l('AuditTeam_TeamStatus')">
              <a-badge
                :status="teamData.status === 1 ? 'success' : 'error'"
                :text="teamData.status === 1 ? l('Enabled') : l('Disabled')"
              />
            </a-descriptions-item>
            <a-descriptions-item :label="l('SortCode')">
              <span class="field-value">{{ teamData.sortCode || '-' }}</span>
            </a-descriptions-item>
            <a-descriptions-item :label="l('CreationTime')">
              <span class="field-value">{{ teamData.creationTime | moment }}</span>
            </a-descriptions-item>
          </a-descriptions>
        </a-card>

        <!-- 团队成员 -->
        <a-card
          :title="l('AuditTeam_TeamMembers')"
          :bordered="false"
          size="small"
          style="margin-top: 16px"
        >
          <template slot="extra">
            <span class="member-count">
              {{ l('AuditTeam_TotalMembers', teamData.memberCount || 0) }}
            </span>
          </template>

          <a-table
            :columns="memberColumns"
            :data-source="teamData.members || []"
            :pagination="false"
            size="small"
            :row-key="record => record.id"
            :locale="{ emptyText: l('AuditTeam_NoTeamMembers') }"
          >
          <!-- 用户名列 -->
            <span slot="userName" slot-scope="text, record">
              <div class="user-info">
                <span class="user-name">{{ text || '-' }}</span>
                <span v-if="record.realName && record.realName !== text" class="real-name">
                  ({{ record.realName }})
                </span>
              </div>
            </span>

            

  
            <!-- 加入时间列 -->
            <span slot="creationTime" slot-scope="text">
              {{ text | moment }}
            </span>
          </a-table>
        </a-card>

        <!-- 操作按钮 -->
        <div class="action-buttons">
          <!-- 只读模式：显示返回按钮 -->
          <template v-if="readonly">
            <a-button @click="handleCancel">
              <a-icon type="arrow-left" /> {{l('Back')}}
            </a-button>
          </template>
          <!-- 编辑模式：显示编辑和管理按钮 -->
          <template v-else-if="isGranted('audit_teams_edit')">
            <a-button type="primary" @click="handleEdit">
              <a-icon type="edit" /> {{l('AuditTeam_EditTeam')}}
            </a-button>
            <a-button @click="handleManageMembers">
              <a-icon type="team" /> {{l('AuditTeam_ManageMembers')}}
            </a-button>
          </template>
        </div>
      </template>
    </a-spin>
  </a-modal>
</template>

<script>
import AppComponentBase from '../../../shared/component-base/app-component-base.js'
import { AuditTeamServiceProxy } from '../../../shared/service-proxies'
import moment from 'moment'

export default {
  mixins: [AppComponentBase],
  name: 'ViewAuditTeam',
  data() {
    return {
      visible: false,
      loading: false,
      teamData: null,
      readonly: false, // 是否为只读模式（从审核任务查看页面进入时为true）

      // 成员表格列配置
      memberColumns: [
        {
          title: this.l('RealName'),
          dataIndex: 'realName',
          key: 'realName',
          width: 80,
          ellipsis: true
        },
        {
          title: this.l('AuditTeam_UserName'),
          dataIndex: 'userName',
          key: 'userName',
          width: 120,
          ellipsis: true
        },
        {
          title: this.l('Role'),
          dataIndex: 'roleName',
          key: 'roleName',
          width: 80,
          align: 'center'
        },
        {
          title: this.l('AuditTeam_JoinTime'),
          dataIndex: 'joinTime',
          key: 'joinTime',
          scopedSlots: { customRender: 'creationTime' },
          width: 150
        }
      ],

      // 服务代理
      serviceProxy: null
    }
  },
  created() {
    this.serviceProxy = new AuditTeamServiceProxy(this.$apiUrl, this.$api)
  },
  methods: {
    // 显示模态框
    open(id, readonly = false) {
      this.visible = true
      this.loading = true
      this.readonly = readonly
      this.loadTeamDetail(id)
    },

    // 加载团队详情
    loadTeamDetail(id) {
      this.serviceProxy.get(id)
        .then(result => {
          this.teamData = result
          // 同时加载团队成员详细信息
          this.loadTeamMembers(id)
        })
        .catch(error => {
          console.error('获取团队详情失败:', error)
          this.$message.error(this.l('AuditTeam_GetDetailFailed') + (error.message || error))
          this.handleCancel()
        })
        .finally(() => {
          this.loading = false
        })
    },

    // 加载团队成员
    loadTeamMembers(id) {
      this.serviceProxy.getTeamMembers(id)
        .then(members => {
          this.teamData.members = members || []
          this.teamData.memberCount = members ? members.length : 0
        })
        .catch(error => {
          console.error('获取团队成员失败:', error)
          this.teamData.members = []
          this.teamData.memberCount = 0
        })
    },

    // 关闭模态框
    handleCancel() {
      this.visible = false
      this.teamData = null
      this.loading = false
    },

    // 编辑团队
    handleEdit() {
      if (!this.teamData) return

      // 触发父组件的编辑事件
      this.$emit('edit', this.teamData)
      this.handleCancel()
    },

    // 管理成员
    handleManageMembers() {
      if (!this.teamData) return

      // 触发父组件的成员管理事件
      this.$emit('manageMembers', this.teamData)
      this.handleCancel()
    }
  },
  filters: {
    moment(date) {
      return moment(date).format('YYYY-MM-DD HH:mm:ss')
    }
  }
}
</script>

<style lang="less" scoped>
.field-value {
  font-weight: 500;
  color: #333;
}

.member-count {
  font-size: 14px;
  color: #666;
}

.user-info {
  display: flex;
  align-items: center;
  flex-wrap: wrap;

  .user-name {
    font-weight: 500;
    color: #1890ff;
  }

  .real-name {
    margin-left: 8px;
    color: #666;
    font-size: 12px;
  }
}

.action-buttons {
  margin-top: 24px;
  text-align: center;
  border-top: 1px solid #e8e8e8;
  padding-top: 16px;

  .ant-btn {
    margin: 0 8px;
  }
}

// 覆盖ant-design的样式
:deep(.ant-descriptions-item-label) {
  font-weight: 600;
  color: #666;
  width: 120px;
}

:deep(.ant-descriptions-item-content) {
  color: #333;
}

:deep(.ant-card-head-title) {
  font-weight: 600;
  color: #1890ff;
}

:deep(.ant-table-thead > tr > th) {
  background-color: #fafafa;
  font-weight: 600;
  color: #666;
}
</style>